import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { defaultAboutPageContent, mergeAboutPageContent } from '../content/defaultTenantSitePages';
import { useAuth } from '../context/AuthContext';
import { useClinicProfile } from '../context/ClinicProfileContext';
import { useTenantSlug } from '../context/TenantSlugContext';
import { TenantSitePagesService } from '../services/TenantSitePagesService';
import type { TenantAboutPageContent, TenantSiteTeamMember } from '../types/site-pages';
import { buildTenantPath } from '../utils/tenantSlug';

const sortTeamMembers = (members: TenantSiteTeamMember[]) =>
  [...members]
    .filter((member) => member.active !== false)
    .sort((left, right) => (left.sortOrder ?? Number.MAX_SAFE_INTEGER) - (right.sortOrder ?? Number.MAX_SAFE_INTEGER));

const AboutPage = () => {
  const { selectedClient } = useAuth();
  const { resolvedTenant } = useTenantSlug();
  const { effectiveTenantSlug } = useClinicProfile();
  const [content, setContent] = useState<TenantAboutPageContent>(() => defaultAboutPageContent);
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const loadContent = async () => {
      const selectedClientId = selectedClient?.clientId ?? null;
      const currentTenantSlug = effectiveTenantSlug ?? null;

      if (!selectedClientId && !currentTenantSlug) {
        setContent(defaultAboutPageContent);
        setIsLoadingContent(false);
        return;
      }

      setIsLoadingContent(true);

      try {
        const pageRecord = selectedClientId
          ? await TenantSitePagesService.getByClientId(selectedClientId, 'about')
          : currentTenantSlug
            ? await TenantSitePagesService.getPublicBySlug(currentTenantSlug, 'about')
            : null;

        if (isCancelled) {
          return;
        }

        setContent(mergeAboutPageContent(pageRecord?.contentJson ?? null));
      } catch (error) {
        console.error('Erro ao carregar conteúdo institucional da página sobre:', error);

        if (!isCancelled) {
          setContent(defaultAboutPageContent);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingContent(false);
        }
      }
    };

    void loadContent();

    return () => {
      isCancelled = true;
    };
  }, [effectiveTenantSlug, resolvedTenant?.clientId, selectedClient?.clientId]);

  const teamMembers = useMemo(
    () => sortTeamMembers(content.team?.members ?? defaultAboutPageContent.team?.members ?? []),
    [content.team?.members],
  );

  const heroTitle = content.hero?.title || defaultAboutPageContent.hero?.title || 'Sobre Nós';
  const introBody = content.intro?.body || defaultAboutPageContent.intro?.body || '';
  const missionTitle = content.mission?.title || defaultAboutPageContent.mission?.title || 'Nossa Missão';
  const missionBody = content.mission?.body || defaultAboutPageContent.mission?.body || '';
  const valuesTitle = content.values?.title || defaultAboutPageContent.values?.title || 'Valores';
  const valuesItems = content.values?.items || defaultAboutPageContent.values?.items || [];
  const differentialsTitle = content.differentials?.title || defaultAboutPageContent.differentials?.title || 'Diferenciais';
  const differentialsItems =
    content.differentials?.items || defaultAboutPageContent.differentials?.items || [];
  const historyTitle = content.history?.title || defaultAboutPageContent.history?.title || 'Nossa História';
  const historyParagraphs =
    content.history?.paragraphs || defaultAboutPageContent.history?.paragraphs || [];
  const teamTitle = content.team?.title || defaultAboutPageContent.team?.title || 'Nossa Equipe';
  const ctaTitle = content.cta?.title || defaultAboutPageContent.cta?.title || 'Agende sua Consulta';
  const ctaBody =
    content.cta?.body ||
    defaultAboutPageContent.cta?.body ||
    'Entre em contato conosco e conheça nossos serviços especializados.';
  const ctaLabel =
    content.cta?.primaryCta?.label ||
    defaultAboutPageContent.cta?.primaryCta?.label ||
    'Agendar Agora';
  const ctaPath =
    content.cta?.primaryCta?.path ||
    defaultAboutPageContent.cta?.primaryCta?.path ||
    '/agendar';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">{heroTitle}</h1>

          {isLoadingContent && (
            <div className="mb-6 flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
              Carregando conteúdo institucional...
            </div>
          )}

          <div className="prose prose-lg mb-16">
            <p className="text-gray-600 mb-6">{introBody}</p>

            <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{missionTitle}</h2>
              <p className="text-gray-600">{missionBody}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">{valuesTitle}</h3>
                <ul className="space-y-2 text-gray-600">
                  {valuesItems.map((item) => (
                    <li key={item}>• {item}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">{differentialsTitle}</h3>
                <ul className="space-y-2 text-gray-600">
                  {differentialsItems.map((item) => (
                    <li key={item}>• {item}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">{historyTitle}</h2>
              {historyParagraphs.map((paragraph, index) => (
                <p
                  key={`${index}-${paragraph.slice(0, 24)}`}
                  className={`text-gray-600 ${index < historyParagraphs.length - 1 ? 'mb-4' : ''}`}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          <section className="mb-16 relative">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">{teamTitle}</h2>
            <div className="relative">
              <Swiper
                modules={[Navigation, Pagination]}
                spaceBetween={24}
                slidesPerView={3}
                navigation={{
                  prevEl: '.swiper-button-prev',
                  nextEl: '.swiper-button-next',
                }}
                pagination={{ clickable: true }}
                breakpoints={{
                  320: { slidesPerView: 1, spaceBetween: 16 },
                  640: { slidesPerView: 2, spaceBetween: 20 },
                  1024: { slidesPerView: 3, spaceBetween: 24 },
                  1280: { slidesPerView: 4, spaceBetween: 24 },
                }}
                className="pb-12"
              >
                {teamMembers.map((member, index) => (
                  <SwiperSlide key={member.id || member.name}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.2 }}
                      viewport={{ once: true }}
                      className="bg-white rounded-lg shadow-lg overflow-hidden h-full"
                    >
                      {member.photo?.downloadUrl ? (
                        <div className="aspect-w-3 aspect-h-4 relative">
                          <img
                            src={member.photo.downloadUrl}
                            alt={member.photo.alt || member.name}
                            className="absolute w-full h-full object-cover object-top"
                          />
                        </div>
                      ) : null}

                      <div className="p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{member.name}</h3>
                        <p className="text-primary-600 font-medium mb-2 text-sm">{member.role}</p>

                        {member.bio ? <p className="text-sm text-gray-600 mb-3">{member.bio}</p> : null}

                        <div className="space-y-2">
                          <ul className="space-y-1 text-sm">
                            {member.credentials.map((credential) => (
                              <li key={credential} className="text-gray-600 flex items-start">
                                <span className="text-primary-600 mr-1.5">•</span>
                                {credential}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <button className="swiper-button-prev absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-1.5 shadow-md">
                <ChevronLeft className="w-5 h-5 text-primary-600" />
              </button>
              <button className="swiper-button-next absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white rounded-full p-1.5 shadow-md">
                <ChevronRight className="w-5 h-5 text-primary-600" />
              </button>
            </div>
          </section>

          <div className="bg-primary-600 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">{ctaTitle}</h2>
            <p className="text-primary-100 mb-6">{ctaBody}</p>
            <Link
              to={buildTenantPath(ctaPath, effectiveTenantSlug)}
              className="inline-block bg-white text-primary-600 px-6 py-3 rounded-md font-medium hover:bg-primary-50 transition-colors"
            >
              {ctaLabel}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
